<template>
  <div class="min-h-screen bg-background flex flex-col">
    <!-- Minimal header -->
    <header class="border-b border-border bg-card">
      <div class="container flex h-16 items-center justify-between">
        <NuxtLink :to="$localePath('/')" class="flex items-center gap-2 text-xl font-bold text-foreground">
          <Icon name="ion:paw" class="text-primary" size="28" />
          Bubba
        </NuxtLink>
      </div>
    </header>

    <!-- Error content -->
    <main class="flex-1 flex items-center justify-center relative overflow-hidden">
      <!-- Decorative paw prints -->
      <Icon name="ion:paw" class="absolute left-[5%] top-[10%] text-primary/5 rotate-[-20deg]" size="120" />
      <Icon name="ion:paw" class="absolute right-[8%] top-[15%] text-primary/5 rotate-25" size="90" />
      <Icon name="ion:paw" class="absolute left-[15%] bottom-[8%] text-primary/5 rotate-40" size="100" />
      <Icon name="ion:paw" class="absolute right-[12%] bottom-[12%] text-primary/5 rotate-[-10deg]" size="80" />

      <div class="container relative z-10 py-16">
        <div class="mx-auto max-w-lg text-center">
          <!-- Icon -->
          <div class="mb-4 flex justify-center">
            <div class="flex h-24 w-24 items-center justify-center rounded-full bg-primary/10 md:h-28 md:w-28">
              <Icon :name="errorIcon" class="text-primary" :size="iconSize" />
            </div>
          </div>

          <!-- Error code -->
          <div class="mb-6">
            <span class="text-[7rem] font-extrabold leading-none tracking-tighter text-primary/10 md:text-[9rem]">
              {{ statusCode }}
            </span>
          </div>

          <!-- Title -->
          <h1 class="text-2xl font-bold text-foreground md:text-3xl">
            {{ errorTitle }}
          </h1>

          <!-- Message -->
          <p class="mt-3 text-lg text-muted-foreground">
            {{ errorMessage }}
          </p>

          <!-- Actions -->
          <div class="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button size="lg" class="gap-2 rounded-2xl px-6" @click="handleGoHome">
              <Icon name="tabler:home" size="18" />
              Go Home
            </Button>
            <Button variant="outline" size="lg" class="gap-2 rounded-2xl px-6" @click="handleGoBack">
              <Icon name="tabler:arrow-left" size="18" />
              Go Back
            </Button>
          </div>

          <!-- Fun message -->
          <p v-if="is404" class="mt-10 text-sm text-muted-foreground/60">
            Even our best sniffer dogs couldn't find this page.
          </p>
          <p v-else class="mt-10 text-sm text-muted-foreground/60">
            Our team of highly trained pets is looking into this.
          </p>

          <!-- Dev-only error details -->
          <div v-if="isDev && errorStack" class="mt-8 text-left">
            <details open>
              <summary class="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 hover:text-muted-foreground">
                Stack Trace (dev only)
              </summary>
              <pre class="mt-3 max-h-80 overflow-auto rounded-xl border border-border bg-muted/50 p-4 text-left text-xs text-foreground/80 whitespace-pre-wrap break-words">{{ errorStack }}</pre>
            </details>
          </div>
        </div>
      </div>
    </main>

    <!-- Minimal footer -->
    <footer class="border-t border-border bg-card py-4">
      <div class="container text-center text-sm text-muted-foreground">
        &copy; {{ new Date().getFullYear() }} Bubba. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { clearError, useError, useI18n } from '#imports';
import Button from '#bubba/shared/components/ui/button/Button.vue';

const { $localePath } = useI18n();

defineProps<{
  error: {
    statusCode?: number;
    message?: string;
    url?: string;
  };
}>();

const nuxtError = useError();

const statusCode = computed(() => nuxtError.value?.statusCode ?? 500);
const is404 = computed(() => statusCode.value === 404);

const errorTitle = computed(() => {
  switch (statusCode.value) {
    case 404:
      return 'Page Not Found';
    case 403:
      return 'Access Denied';
    case 500:
      return 'Something Went Wrong';
    case 503:
      return 'Service Unavailable';
    default:
      return 'Oops! An Error Occurred';
  }
});

const errorMessage = computed(() => {
  switch (statusCode.value) {
    case 404:
      return "The page you're looking for doesn't exist or has been moved.";
    case 403:
      return "You don't have permission to access this page.";
    case 503:
      return 'Our service is temporarily unavailable. Please check back soon.';
    default:
      return nuxtError.value?.message || 'An unexpected error occurred.';
  }
});

const isDev = import.meta.dev;
const errorStack = computed(() => (nuxtError.value as any)?.stack ?? null);

const errorIcon = computed(() => {
  switch (statusCode.value) {
    case 404:
      return 'tabler:mood-sad';
    case 403:
      return 'tabler:lock';
    case 500:
      return 'tabler:bug';
    case 503:
      return 'tabler:cloud-off';
    default:
      return 'tabler:alert-triangle';
  }
});

const iconSize = computed(() => {
  return typeof window !== 'undefined' && window.innerWidth >= 768 ? '56' : '48';
});

function handleGoHome() {
  clearError({ redirect: '/' });
}

function handleGoBack() {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    clearError();
    window.history.back();
  } else {
    clearError({ redirect: '/' });
  }
}
</script>
